import { MutableRefObject, useEffect } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { FieldPath, FieldValues, Path, PathValue, RegisterOptions, UseFormRegister, UseFormReturn } from "react-hook-form";

import { inputCss, selectCss } from "./native.comp";

export enum EditingMode {
  create,
  edit,
}

interface ICustomInputProps<T extends FieldValues> extends React.InputHTMLAttributes<HTMLInputElement> {
  name: FieldPath<T>;
  label?: string;
  type: "text" | "number" | "email" | "password" | "date" | "time" | "datetime-local" | "search" | "tel" | "url";
  methods: UseFormReturn<T>;
  placeholder?: string;
  disabled?: boolean;
  fixedWidth?: boolean;
}

export function FancyLabelAndInput<T extends FieldValues>({
  name,
  label,
  type,
  methods,
  placeholder,
  disabled,
  fixedWidth = false,
}: ICustomInputProps<T>) {
  const { errors } = methods.formState;

  const inputClass =
    "h-11 border w-full my-3 px-2 rounded-md mb-1 dark:text-gray-500 " +
    "hover:border-blue-200 hover:border-2 " +
    "focus-visible:outline-none focus-visible:border-blue-600 focus-visible:border-2 focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow]";

  const FixedWidthInputClass =
    "h-11 border w-14 my-3 px-2 rounded-md mb-1 dark:text-gray-500 " +
    "hover:border-blue-200 hover:border-2 " +
    "focus-visible:outline-none focus-visible:border-blue-600 focus-visible:border-2 focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow]";

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isCtrlKey = event.ctrlKey || event.metaKey; // Check if Ctrl or Command key is pressed (for macOS)
    const isText = /[a-zA-Z]/.test(event.key); // Check if the pressed key is a text character
    const allowedSpecialChars = ["Tab", "+", "-"]; // Allowed keys

    // Allow Tab key, +, and - to work
    if (allowedSpecialChars.includes(event.key)) {
      return; // Do nothing, allow these keys
    }

    // Prevent text and special characters except the allowed ones
    if (!isCtrlKey && (isText || /[^0-9]/.test(event.key)) && event.key !== "Backspace") {
      event.preventDefault();
    }
  };

  return (
    <div className="space-y-1 w-full">
      <label className="text-sm dark:text-white">{label}</label>

      {type === "number" ? (
        <input
          className={fixedWidth ? FixedWidthInputClass : inputClass}
          {...methods.register(name)}
          placeholder={placeholder}
          disabled={disabled}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <input
          className={fixedWidth ? FixedWidthInputClass : inputClass}
          {...methods.register(name)}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}

      <ErrorMessage
        errors={errors}
        name={name as any}
        render={({ message }) => <p className="text-red-400 text-right font-bold text-xs">{message}</p>}
      />
    </div>
  );
}

export function FancyTextInput({
  register,
  placeholder,
  type = "text",
  step,
  className,
  required,
  inputRef,
}: {
  register: any;
  placeholder?: string;
  type?: string;
  step?: string;
  className?: string;
  required?: boolean;
  inputRef?: MutableRefObject<HTMLDivElement | undefined>;
}) {
  return (
    <input
      className={`${inputCss} ${className ?? ""} w-full my-0 px-4 h-10 outline-none `}
      type={type}
      step={step}
      {...register({
        ref: inputRef,
      })}
      placeholder={placeholder}
      required={required}
    />
  );
}

export function FancyLabelAndTextArea<T extends FieldValues>({
  name,
  label,
  methods,
  placeholder,
  disabled,
  rows = 3,
  fixedWidth = false,
}: {
  name: FieldPath<T>;
  label?: string;
  rows?: number;
  methods: UseFormReturn<T>;
  placeholder?: string;
  disabled?: boolean;
  fixedWidth?: boolean;
}) {
  const { errors } = methods.formState;

  const inputClass =
    "border w-full my-3 px-2 rounded-md mb-1 dark:text-gray-500 " +
    "hover:border-blue-200 hover:border-2 " +
    "focus-visible:outline-none focus-visible:border-blue-600 focus-visible:border-2 focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow]";

  const FixedWidthInputClass =
    "border w-14 my-3 px-2 rounded-md mb-1 dark:text-gray-500 " +
    "hover:border-blue-200 hover:border-2 " +
    "focus-visible:outline-none focus-visible:border-blue-600 focus-visible:border-2 focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow]";

  return (
    <div className="space-y-1 w-full">
      <label className="text-sm dark:text-white">{label}</label>

      <textarea
        className={fixedWidth ? FixedWidthInputClass : inputClass}
        {...methods.register(name)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
      />

      <ErrorMessage
        errors={errors}
        name={name as any}
        render={({ message }) => <p className="text-red-400 text-right font-bold text-xs">{message}</p>}
      />
    </div>
  );
}

export interface OptionItem {
  label: string;
  value: string | number | string[];
  disabled?: boolean;
}

export function FancySelect<T extends FieldValues>({
  register,
  fieldName,
  registerOptions,
  children,
  className,
  ...restProps
}: {
  register: UseFormRegister<T>;
  fieldName: Path<T>;
  registerOptions?: RegisterOptions<T, any>;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <select {...register(fieldName, registerOptions)} {...restProps} className={`${selectCss} ${className}`}>
      {children}
    </select>
  );
}

export function LabelAndSelect<T extends FieldValues>({
  methods,
  label,
  fieldName,
  registerOptions,
  options,
  setDefault = false,
  defaultValue,
  topLabelCss = "block mt-4 w-full",
  selectCss: selectClassName = "w-full",
}: {
  methods: UseFormReturn<T>;
  label: string;
  fieldName: Path<T>;
  registerOptions?: RegisterOptions<T, any>;
  options: OptionItem[];
  setDefault?: boolean;
  defaultValue?: PathValue<T, Path<T>>;
  topLabelCss?: string;
  selectCss?: string;
}) {
  useEffect(() => {
    if (!setDefault) return;

    if (defaultValue) {
      methods.setValue(fieldName, defaultValue);
    } else if (options && options.length > 0) {
      methods.setValue(fieldName, options[0].value as PathValue<T, Path<T>>);
    } else {
      methods.setValue(fieldName, null as PathValue<T, Path<T>>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mirror her-admin: react to option list changes only
  }, [options]);

  return (
    <label className={topLabelCss}>
      <span className="block mb-2">{label}</span>
      <FancySelect<T>
        className={selectClassName}
        register={methods.register}
        fieldName={fieldName}
        registerOptions={registerOptions}
      >
        {options?.map((o, i) => (
          <option key={i} value={o.value as string | number} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </FancySelect>
    </label>
  );
}
